import { motion } from "framer-motion";
import { Heart } from "lucide-react";
import { imgSrc } from "./repApi";

/* দোকানের তাক — বড় ভিজ্যুয়াল, কার্ড-গ্রিড নয়; পাঞ্জাবিরা আলমারিতে, বাকিরা পাল-দেওয়ালে */
export default function Shelf({ products, saved, toggleSave, onOpen, onTryon, spotlight, shelfRef }) {
  const panjabis = products.filter((p) => p.tryon);
  const others = products.filter((p) => !p.tryon);
  const [first, ...rest] = panjabis;

  const Hearts = ({ p }) => {
    const isSaved = saved.includes(p.id);
    return (
      <button
        data-testid={`shelf-save-${p.id}`}
        onClick={(e) => {
          e.stopPropagation();
          toggleSave(p.id);
        }}
        aria-label={isSaved ? "পছন্দ থেকে বাদ দিন" : "পছন্দে রাখুন"}
        className="rounded-full border border-gold/25 bg-dusk/60 p-2.5 backdrop-blur-md transition-transform duration-300 hover:scale-110"
      >
        <Heart size={15} className={isSaved ? "fill-vermilion text-vermilion" : "text-sand/80"} />
      </button>
    );
  };

  return (
    <section ref={shelfRef} className="mx-auto max-w-[1300px] px-5 pb-20 pt-4 sm:px-10">
      <div className="mb-8 flex items-end justify-between gap-4">
        <div>
          <p className="font-latin text-xs italic tracking-[0.25em] text-gold/80">THE SHELVES</p>
          <h2 className="mt-2 font-display text-3xl text-sand sm:text-4xl">দোকানের তাক</h2>
        </div>
        <motion.p
          animate={spotlight ? { opacity: [0.6, 1, 0.6] } : { opacity: 0 }}
          transition={{ repeat: 2, duration: 1.6 }}
          className="hidden rounded-full border border-gold/40 bg-gold/10 px-4 py-1.5 text-xs text-gold sm:block"
        >
          এই পাঞ্জাবিগুলো গায়ে পরে দেখা যায়
        </motion.p>
      </div>

      {/* আলমারি — তিন পাঞ্জাবি, একটা বড় ফিচার্ড */}
      <div
        data-testid="panjabi-almirah"
        className={`grid gap-5 rounded-2xl border p-4 transition-shadow duration-700 sm:p-6 lg:grid-cols-5 ${
          spotlight ? "shadow-[0_0_0_2px_rgba(232,181,77,0.5),0_18px_60px_rgba(232,181,77,0.15)]" : "border-gold/15"
        } bg-earthen/60`}
      >
        {first && (
          <motion.article
            key={first.id}
            data-testid={`shop-product-${first.id}`}
            onClick={() => onOpen(first)}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="group cursor-pointer lg:col-span-3"
          >
            <div className="relative aspect-[3/4] overflow-hidden rounded-xl">
              <img src={imgSrc(first.img)} alt={first.title} className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.05]" />
              <div className="absolute right-3 top-3">
                <Hearts p={first} />
              </div>
              <span className="absolute left-3 top-3 rounded-full bg-alpana/95 px-3 py-1 text-[10px] font-medium text-dusk">
                গায়ে পরে দেখুন
              </span>
            </div>
            <div className="mt-4 flex flex-wrap items-baseline justify-between gap-2 border-b-2 border-[#7a4a28]/70 pb-3">
              <div>
                <h3 className="font-display text-2xl leading-snug text-sand">{first.title}</h3>
                <p className="mt-1 text-xs text-sand/55">{first.craft}</p>
              </div>
              <span className="font-display text-xl text-gold">{first.price}</span>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-sand/70">{first.story}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                data-testid={`shelf-view-${first.id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onOpen(first);
                }}
                className="rounded-full border border-alpana/40 bg-dusk/40 px-5 py-2 text-sm text-alpana transition-all duration-300 hover:border-gold hover:bg-gold hover:text-dusk"
              >
                দেখুন
              </button>
              <button
                data-testid={`shelf-tryon-${first.id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onTryon(first);
                }}
                className="rounded-full bg-vermilion/90 px-5 py-2 text-sm text-alpana transition-all duration-300 hover:bg-vermilion"
              >
                নিজের উপর দেখে নিন
              </button>
            </div>
          </motion.article>
        )}

        <div className="flex flex-col gap-6 lg:col-span-2">
          {rest.map((p, i) => (
            <motion.article
              key={p.id}
              data-testid={`shop-product-${p.id}`}
              onClick={() => onOpen(p)}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ delay: i * 0.1, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="group flex cursor-pointer flex-1 gap-4"
            >
              <div className="relative h-40 w-32 shrink-0 overflow-hidden rounded-xl sm:h-48 sm:w-40">
                <img src={imgSrc(p.img)} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.05]" />
                <div className="absolute right-2 top-2">
                  <Hearts p={p} />
                </div>
              </div>
              <div className="flex min-w-0 flex-1 flex-col justify-center border-b-2 border-[#7a4a28]/70 pb-2">
                <h3 className="font-display text-lg leading-snug text-sand">{p.title}</h3>
                <p className="mt-1 text-xs text-sand/55">{p.craft}</p>
                <span className="mt-2 font-display text-lg text-gold">{p.price}</span>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    data-testid={`shelf-view-${p.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpen(p);
                    }}
                    className="rounded-full border border-gold/35 px-3.5 py-1.5 text-xs text-gold transition-colors hover:bg-gold hover:text-dusk"
                  >
                    দেখুন
                  </button>
                  <button
                    data-testid={`shelf-tryon-${p.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onTryon(p);
                    }}
                    className="rounded-full border border-alpana/35 px-3.5 py-1.5 text-xs text-alpana transition-colors hover:bg-alpana hover:text-dusk"
                  >
                    নিজের উপর
                  </button>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>

      {/* পাল-দেওয়াল — চিত্রপট আর কাঁথা */}
      <div className="mt-10 grid gap-5 md:grid-cols-2">
        {others.map((p, i) => (
          <motion.article
            key={p.id}
            data-testid={`shop-product-${p.id}`}
            onClick={() => onOpen(p)}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ delay: i * 0.08, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className={`group cursor-pointer overflow-hidden rounded-2xl border border-gold/15 bg-earthen/60 ${i % 2 ? "md:translate-y-6" : ""}`}
          >
            <div className="relative aspect-[4/3] overflow-hidden">
              <img src={imgSrc(p.img)} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.05]" />
              <div className="absolute right-3 top-3">
                <Hearts p={p} />
              </div>
            </div>
            <div className="border-b-2 border-[#7a4a28]/70 p-5">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h3 className="font-display text-xl leading-snug text-sand">{p.title}</h3>
                <span className="font-display text-lg text-gold">{p.price}</span>
              </div>
              <p className="mt-1 text-xs text-sand/55">{p.craft} • {p.artisan}</p>
              <p className="mt-2 line-clamp-2 text-sm text-sand/65">{p.story}</p>
              <button
                data-testid={`shelf-view-${p.id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onOpen(p);
                }}
                className="mt-3 rounded-full border border-alpana/40 px-4 py-1.5 text-xs text-alpana transition-colors hover:bg-alpana hover:text-dusk"
              >
                দেখুন
              </button>
            </div>
          </motion.article>
        ))}
      </div>
    </section>
  );
}
